import { useState } from 'react';
import {
  App,
  Button,
  Card,
  Dropdown,
  Input,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps, MenuProps } from 'antd';
import {
  AppstoreAddOutlined,
  CheckCircleOutlined,
  EditOutlined,
  LockOutlined,
  MobileOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useDeactivateProfile, useProfiles } from '@/hooks/useProfiles';
import { profilesApi } from '@/api/profilesApi';
import { useEnterprises } from '@/hooks/useEnterprises';
import { useAuthStore } from '@/stores/authStore';
import type { ProfileDto } from '@/types/profile';
import { ProfileFormModal } from './ProfileFormModal';
import { ProfileAppsModal } from './ProfileAppsModal';
import ProfileRestrictionsModal from './ProfileRestrictionsModal';

const { Title, Text } = Typography;

export function ProfilesPage() {
  const { message } = App.useApp();
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [enterpriseFilter, setEnterpriseFilter] = useState<number | undefined>();

  const [formOpen, setFormOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<ProfileDto | null>(null);
  const [appsManagerProfile, setAppsManagerProfile] = useState<ProfileDto | null>(null);
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [restrictionsProfile, setRestrictionsProfile] = useState<ProfileDto | null>(null);

  const { data, isLoading, refetch, isFetching } = useProfiles({
    page,
    pageSize,
    search: search || undefined,
    includeInactive,
    enterpriseId: enterpriseFilter,
  });

  const { data: enterprises } = useEnterprises({ pageSize: 100 });

  const deactivate = useDeactivateProfile();

  const handleDeactivate = async (p: ProfileDto) => {
    try {
      await deactivate.mutateAsync(p.id);
      message.success(`Perfil "${p.displayNameEs}" desactivado.`);
    } catch {
      // interceptor
    }
  };

  async function handleSync(p: ProfileDto) {
    try {
      setSyncingId(p.id);
      await profilesApi.sync(p.id);
      message.success(`Perfil ${p.displayNameEs} sincronizado con Google.`);
      refetch();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error al sincronizar.');
    } finally {
      setSyncingId(null);
    }
  }

  const columns: TableProps<ProfileDto>['columns'] = [
    {
      title: 'Perfil',
      key: 'profile',
      render: (_, p) => (
        <Space direction="vertical" size={0}>
          <Space>
            <SafetyCertificateOutlined style={{ color: 'var(--color-primary)' }} />
            <Text strong>{p.displayNameEs}</Text>
          </Space>
          <Text code style={{ fontSize: 12 }}>{p.name}</Text>
        </Space>
      ),
    },
    ...(isSuperAdmin || !enterpriseFilter ? [{
      title: 'Enterprise',
      dataIndex: 'enterpriseName',
      key: 'enterpriseName',
      width: 200,
    } as TableProps<ProfileDto>['columns'][number]] : []),
    {
      title: 'Apps',
      dataIndex: 'appsCount',
      key: 'appsCount',
      width: 100,
      align: 'center',
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: 'Dispositivos',
      dataIndex: 'devicesCount',
      key: 'devicesCount',
      width: 130,
      align: 'center',
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: 'Sync con Google',
      dataIndex: 'lastSyncAt',
      key: 'lastSyncAt',
      width: 180,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : <Text type="secondary">Nunca</Text>,
    },
    {
      title: 'Estado',
      key: 'state',
      width: 110,
      render: (_, p) => p.isActive ? <Tag color="success">Activo</Tag> : <Tag color="error">Inactivo</Tag>,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 100,
      align: 'right',
      render: (_, p) => {
        const items: MenuProps['items'] = [
          {
            key: 'apps',
            label: `Apps (${p.appsCount})`,
            icon: <AppstoreAddOutlined />,
            onClick: () => setAppsManagerProfile(p),
          },
          {
            key: 'restrictions',
            label: 'Restricciones',
            icon: <LockOutlined />,
            onClick: () => setRestrictionsProfile(p),
          },
          {
            key: 'edit',
            label: 'Editar',
            icon: <EditOutlined />,
            onClick: () => {
              setEditingProfile(p);
              setFormOpen(true);
            },
          },
          {
            key: 'sync',
            label: syncingId === p.id ? 'Sincronizando...' : 'Sincronizar con Google',
            icon: <ReloadOutlined spin={syncingId === p.id} />,
            onClick: () => handleSync(p),
          },
          { type: 'divider' as const },
          ...(p.isActive ? [{
            key: 'deactivate',
            label: 'Desactivar',
            icon: <StopOutlined />,
            danger: true,
            onClick: () => handleDeactivate(p),
          }] : []),
        ];
        return (
          <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <SafetyCertificateOutlined style={{ marginRight: 12 }} />
          Perfiles
        </Title>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => {
            setEditingProfile(null);
            setFormOpen(true);
          }}
        >
          Nuevo Perfil
        </Button>
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <Space wrap>
            <Input
              placeholder="Buscar perfiles..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => setPage(1)}
              style={{ width: 280 }}
              allowClear
            />
            <Select
              placeholder="Filtrar por Enterprise"
              allowClear
              value={enterpriseFilter}
              onChange={(v) => { setEnterpriseFilter(v); setPage(1); }}
              options={(enterprises?.items ?? []).map((e) => ({ value: e.id, label: e.displayNameEs }))}
              style={{ width: 240 }}
            />
            <Button onClick={() => { setPage(1); refetch(); }}>Buscar</Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Refrescar
            </Button>
          </Space>

          <Space>
            <Text>Incluir inactivos:</Text>
            <Switch checked={includeInactive} onChange={(v) => { setIncludeInactive(v); setPage(1); }} />
          </Space>
        </div>

        <Table<ProfileDto>
          rowKey="id"
          columns={columns}
          dataSource={data?.items ?? []}
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total: data?.totalCount ?? 0,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total) => `${total} en total`,
          }}
        />
      </Card>

      <ProfileFormModal
        open={formOpen}
        profile={editingProfile}
        onClose={() => {
          setFormOpen(false);
          setEditingProfile(null);
        }}
      />

      <ProfileAppsModal
        profile={appsManagerProfile}
        onClose={() => setAppsManagerProfile(null)}
      />

      <ProfileRestrictionsModal
        open={restrictionsProfile !== null}
        profileId={restrictionsProfile?.id ?? null}
        profileName={restrictionsProfile?.displayNameEs}
        onClose={() => setRestrictionsProfile(null)}
      />
    </div>
  );
}
